<!-- frontend/src/components/Financeiro/Conciliacao/DesfazerConciliacaoModal.vue -->
<template>
  <div class="modal-overlay" @click.self="$emit('close')">
    <div class="modal-container">
      <div class="modal-header">
        <div>
          <h6 class="mb-0">Desfazer conciliação</h6>
          <small class="modal-subtitle">
            Revise as opções antes de desfazer este vínculo financeiro.
          </small>
        </div>

        <i class="bi bi-x-circle close-icon" @click="$emit('close')"></i>
      </div>

      <div class="modal-content">
        <div class="alert alert-warning py-2 mb-3">
          <strong>Atenção:</strong>
          esta ação irá desfazer os vínculos deste lançamento na conciliação bancária.
        </div>

        <div class="info-box mb-3">
          <div class="info-row">
            <span class="info-label">Origem:</span>
            <span>{{ origemTipo || '—' }}</span>
          </div>

          <div class="info-row">
            <span class="info-label">Data:</span>
            <span>{{ formatDate(dataExibicao) }}</span>
          </div>

          <div class="info-row">
            <span class="info-label">Valor:</span>
            <span>{{ formatCurrency(valorExibicao) }}</span>
          </div>

          <div class="info-row">
            <span class="info-label">Memo:</span>
            <span>{{ memoExibicao }}</span>
          </div>

          <div class="info-row">
            <span class="info-label">Status atual:</span>
            <span>{{ formatStatus(item?.status_conciliacao) }}</span>
          </div>

          <div class="info-row">
            <span class="info-label">Vínculo:</span>
            <span>{{ item?.movimento_label_amigavel || '—' }}</span>
          </div>
        </div>

        <div
          v-if="possuiReceitaCriadaConciliacao"
          class="delete-created-revenue-box mb-3"
        >
          <label class="delete-created-revenue-label">
            <input
              v-model="excluirReceitaCriada"
              type="checkbox"
              class="delete-created-revenue-checkbox"
            />

            <div class="delete-created-revenue-content">
              <div class="delete-created-revenue-title">
                Excluir também a Receita Geral criada por esta conciliação
              </div>

              <div class="delete-created-revenue-description">
                Esta conciliação criou uma Receita Geral automaticamente. Se esta opção estiver marcada,
                o sistema tentará excluir essa receita ao desfazer a conciliação.
              </div>
            </div>
          </label>
        </div>

        <div
          v-if="possuiReceitaAbatimentoCriadaConciliacao"
          class="delete-created-revenue-box mb-3"
        >
          <label class="delete-created-revenue-label">
            <input
              v-model="excluirReceitaAbatimentoCriada"
              type="checkbox"
              class="delete-created-revenue-checkbox"
            />

            <div class="delete-created-revenue-content">
              <div class="delete-created-revenue-title">
                Excluir também a Receita de abatimento criada por esta conciliação
              </div>

              <div class="delete-created-revenue-description">
                Esta conciliação criou uma Receita Geral para abater o pagamento consolidado.
                Se esta opção estiver marcada, o sistema tentará excluir essa receita ao desfazer a conciliação.
              </div>
            </div>
          </label>
        </div>        

        <div
          v-if="possuiDespesaCriadaConciliacao"
          class="delete-created-expense-box mb-3"
        >
          <label class="delete-created-revenue-label">
            <input
              v-model="excluirDespesaCriada"
              type="checkbox"
              class="delete-created-revenue-checkbox"
            />

            <div class="delete-created-revenue-content">
              <div class="delete-created-revenue-title">
                Excluir também a Despesa criada por esta conciliação
              </div>

              <div class="delete-created-revenue-description">
                Esta conciliação criou uma Despesa automaticamente. Se esta opção estiver marcada,
                o sistema tentará excluir essa despesa ao desfazer a conciliação.
              </div>
            </div>
          </label>
        </div>
        
        <div
          v-if="possuiAjustePagamentoConciliacao"
          class="payment-adjustment-info-box mb-3"
        >
          <div class="payment-adjustment-title">
            Ajustes de pagamento serão mantidos
          </div>

          <div class="payment-adjustment-description">
            Esta conciliação ajustou juros, multa ou desconto em pagamento(s) de despesa.
            Ao desfazer a conciliação, os vínculos serão removidos, mas esses ajustes financeiros serão mantidos no lançamento.
          </div>
        </div>        
        
        <div class="form-group mb-0">
          <label>Motivo / observação</label>
          <textarea
            v-model="motivo"
            class="form-control"
            rows="3"
            placeholder="Opcional. Ex.: lançamento conciliado incorretamente."
          ></textarea>
        </div>
      </div>

      <div class="modal-footer">
        <button
          class="btn btn-secondary btn-sm me-2"
          type="button"
          :disabled="salvando"
          @click="$emit('close')"
        >
          Cancelar
        </button>

        <button
          class="btn btn-danger btn-sm"
          type="button"
          :disabled="salvando"
          @click="confirmar"
        >
          {{ salvando ? 'Desfazendo...' : 'Desfazer conciliação' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'

const props = defineProps({
  origemTipo: { type: String, default: '' },
  item: { type: Object, default: null },
  salvando: { type: Boolean, default: false },
  possuiReceitaCriadaConciliacao: { type: Boolean, default: false },
  possuiReceitaAbatimentoCriadaConciliacao: { type: Boolean, default: false },
  possuiDespesaCriadaConciliacao: { type: Boolean, default: false },
  possuiAjustePagamentoConciliacao: { type: Boolean, default: false }
})

const emit = defineEmits(['close', 'confirmar'])

const excluirReceitaCriada = ref(true)
const excluirReceitaAbatimentoCriada = ref(true)
const excluirDespesaCriada = ref(true)
const motivo = ref('')

const dataExibicao = computed(() => {
  const origem = String(props.origemTipo || '').toUpperCase()

  if (origem === 'OPERADORA') {
    return (
      props.item?.data_vencimento ||
      props.item?.data_venda ||
      null
    )
  }

  return props.item?.dt_movimento || null
})

const valorExibicao = computed(() => {
  const origem = String(props.origemTipo || '').toUpperCase()

  if (origem === 'OPERADORA') {
    return (
      props.item?.valor_parcela ??
      props.item?.valor_bruto ??
      props.item?.valor_liquido ??
      0
    )
  }

  return props.item?.valor ?? 0
})

const memoExibicao = computed(() => {
  const origem = String(props.origemTipo || '').toUpperCase()

  if (origem === 'OPERADORA') {
    const operadora =
      String(props.item?.operadora_nome || 'Operadora').trim()

    const autorizacao =
      String(props.item?.autorizacao || '').trim()

    if (autorizacao) {
      return `${operadora} · Autorização ${autorizacao}`
    }

    return (
      props.item?.observacoes ||
      props.item?.tipo_evento ||
      '—'
    )
  }

  return props.item?.memo || '—'
})


function confirmar() {
  emit('confirmar', {
    excluir_receita_criada: props.possuiReceitaCriadaConciliacao
      ? excluirReceitaCriada.value
      : false,

    excluir_receita_abatimento_criada: props.possuiReceitaAbatimentoCriadaConciliacao
      ? excluirReceitaAbatimentoCriada.value
      : false,

    excluir_despesa_criada: props.possuiDespesaCriadaConciliacao
      ? excluirDespesaCriada.value
      : false,

    motivo: motivo.value || null
  })
}

function formatDate(v) {
  if (!v) return '—'

  const s = String(v).slice(0, 10)

  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const [y, m, d] = s.split('-')
    return `${d}/${m}/${y}`
  }

  const dt = new Date(v)
  if (Number.isNaN(dt.getTime())) return '—'

  const d = String(dt.getDate()).padStart(2, '0')
  const m = String(dt.getMonth() + 1).padStart(2, '0')
  const y = dt.getFullYear()

  return `${d}/${m}/${y}`
}

function formatCurrency(v) {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(Number(v || 0))
}

function formatStatus(v) {
  const status = String(v || '').toUpperCase()

  const map = {
    PENDENTE: 'Pendente',
    CONCILIADO_AUTOMATICO: 'Conciliado automático',
    CONCILIADO_MANUAL: 'Conciliado manual',
    IGNORADO: 'Ignorado'
  }

  return map[status] || status.replaceAll('_', ' ') || '—'
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-container {
  background: #fff;
  width: 100%;
  max-width: 620px;
  max-height: 92vh;
  margin: 0 12px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 0;
}

.modal-header {
  background: #f3f4f6;
  padding: 10px 15px;
  border-bottom: 1px solid #ced4da;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-subtitle {
  font-size: 0.78rem;
  color: #6b7280;
}

.modal-content {
  padding: 15px;
  overflow-y: auto;
}

.modal-footer {
  padding: 10px 15px;
  border-top: 1px solid #ced4da;
  display: flex;
  justify-content: flex-end;
  background: #fff;
}

.close-icon {
  font-size: 1.4rem;
  cursor: pointer;
}

.info-box {
  border: 1px solid #d1d5db;
  background: #f8fafc;
  padding: 10px 12px;
}

.info-row {
  display: flex;
  gap: 8px;
  margin-bottom: 4px;
  font-size: 0.86rem;
}

.info-row:last-child {
  margin-bottom: 0;
}

.info-label {
  min-width: 90px;
  font-weight: 700;
  color: #374151;
}

.delete-created-revenue-box {
  border: 1px solid #fed7aa;
  background: #fff7ed;
  padding: 12px;
}

.delete-created-expense-box {
  border: 1px solid #fecaca;
  background: #fef2f2;
  padding: 12px;
}

.delete-created-revenue-label {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin: 0;
  cursor: pointer;
}

.delete-created-revenue-checkbox {
  width: 18px;
  height: 18px;
  margin-top: 2px;
  cursor: pointer;
  flex-shrink: 0;
}

.delete-created-revenue-content {
  flex: 1;
}

.delete-created-revenue-title {
  font-weight: 800;
  color: #111827;
  font-size: 0.88rem;
  line-height: 1.2;
}

.delete-created-revenue-description {
  color: #6b7280;
  font-size: 0.78rem;
  line-height: 1.35;
  margin-top: 4px;
}

.form-group label {
  font-weight: 700;
  margin-bottom: 5px;
  display: block;
}

.form-control {
  border-radius: 0;
}

.payment-adjustment-info-box {
  border: 1px solid #fed7aa;
  background: #fff7ed;
  padding: 12px;
}

.payment-adjustment-title {
  font-weight: 800;
  color: #111827;
  font-size: 0.88rem;
  line-height: 1.2;
}

.payment-adjustment-description {
  color: #6b7280;
  font-size: 0.78rem;
  line-height: 1.35;
  margin-top: 4px;
}
</style>